import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, ShoppingBasket } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useBasket } from "@/lib/BasketContext";

export const NAV_LINKS = [
  { to: "/shop", label: "Shop Bakes", testid: "nav-shop-link" },
  { to: "/about", label: "Our Story", testid: "nav-about-link" },
  { to: "/events", label: "Grazing Tables", testid: "nav-events-link" },
  { to: "/faqs", label: "FAQs", testid: "nav-faqs-link" },
  { to: "/delivery-collection", label: "Delivery & Collection", testid: "nav-delivery-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

const linkClass = ({ isActive }) =>
  `text-sm font-semibold transition-colors duration-150 hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus focus-visible:ring-offset-2 focus-visible:ring-offset-choco rounded-md px-1 py-0.5 ${
    isActive ? "text-gold" : "text-cream-text"
  }`;

export const Header = () => {
  const { count } = useBasket();
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-choco-line bg-choco/90 backdrop-blur-md" data-testid="site-header">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:h-20 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus" data-testid="header-logo-link">
          <img src="/assets/logo.png" alt="Bakes by CR" className="h-10 w-auto sm:h-12" />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex" aria-label="Main navigation">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkClass} data-testid={l.testid}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/basket"
            className="relative inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-bold text-choco transition-colors duration-150 hover:bg-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus focus-visible:ring-offset-2 focus-visible:ring-offset-choco active:scale-[0.98]"
            data-testid="header-basket-button"
          >
            <ShoppingBasket className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Basket</span>
            {count > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-cream px-1 text-[11px] font-bold text-choco" data-testid="header-basket-count">
                {count}
              </span>
            )}
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-choco-line text-cream-text transition-colors hover:border-gold hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus lg:hidden"
                aria-label="Open menu"
                data-testid="mobile-menu-button"
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 border-choco-line bg-choco-light text-cream-text">
              <SheetHeader>
                <SheetTitle className="font-serif text-xl text-cream-text">
                  <img src="/assets/logo.png" alt="Bakes by CR" className="h-10 w-auto" />
                </SheetTitle>
                <SheetDescription className="sr-only">Main navigation menu</SheetDescription>
              </SheetHeader>
              <nav className="mt-8 flex flex-col gap-5" aria-label="Mobile navigation">
                {NAV_LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `text-lg font-semibold ${isActive ? "text-gold" : "text-cream-text"} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus rounded-md`
                    }
                    data-testid={`mobile-${l.testid}`}
                  >
                    {l.label}
                  </NavLink>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
